import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Vehicles from "@/pages/Vehicles";
import VehicleDetail from "@/pages/VehicleDetail";
import InspectionFlow from "@/pages/InspectionFlow";
import InspectionHistory from "@/pages/InspectionHistory";
import Maintenance from "@/pages/Maintenance";
import Operations from "@/pages/Operations";
import Fuel from "@/pages/Fuel";
import Notifications from "@/pages/Notifications";
import Mailbox from "@/pages/Mailbox";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import Partners from "@/pages/Partners";
import AuditLogs from "@/pages/AuditLogs";
import Settings from "@/pages/Settings";
import DriverScores from "@/pages/DriverScores";
import DepartmentHeatmap from "@/pages/DepartmentHeatmap";
import GpsTracking from "@/pages/GpsTracking";
import Webmail from "@/pages/Webmail";
import Billing from "@/pages/Billing";
import SuperAdmin from "@/pages/SuperAdmin";
import Calendar from "@/pages/Calendar";
import InfoPage from "@/pages/InfoPage";
import FaviconSync from "@/components/FaviconSync";

function Shell({ Page, roles }) {
    return (
        <ProtectedRoute roles={roles}>
            <AppShell>
                <Page />
            </AppShell>
        </ProtectedRoute>
    );
}

function RootRedirect() {
    const { user } = useAuth();
    if (user === null) return null; // loading
    if (!user) return <Landing />;
    if (user.role === "super_admin") return <Navigate to="/admin" replace />;
    return <Navigate to="/dashboard" replace />;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <FaviconSync />
                    <Toaster position="top-right" richColors closeButton />
                    <Routes>
                        <Route path="/" element={<RootRedirect />} />
                        <Route path="/info/:slug" element={<InfoPage />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/dashboard" element={<Shell Page={Dashboard} />} />
                        <Route path="/vehicles" element={<Shell Page={Vehicles} />} />
                        <Route path="/vehicles/:id" element={<Shell Page={VehicleDetail} />} />
                        <Route path="/inspect" element={<Shell Page={InspectionFlow} />} />
                        <Route path="/inspections" element={<Shell Page={InspectionHistory} />} />
                        <Route path="/maintenance" element={<Shell Page={Maintenance} />} />
                        <Route path="/operations" element={<Shell Page={Operations} />} />
                        <Route path="/fuel" element={<Shell Page={Fuel} />} />
                        <Route path="/notifications" element={<Shell Page={Notifications} />} />
                        <Route path="/mailbox" element={<Shell Page={Mailbox} />} />
                        <Route path="/calendar" element={<Shell Page={Calendar} />} />
                        <Route path="/reports" element={<Shell Page={Reports} />} />
                        <Route path="/driver-scores" element={<Shell Page={DriverScores} />} />
                        <Route path="/department-heatmap" element={<Shell Page={DepartmentHeatmap} />} />
                        <Route path="/gps" element={<Shell Page={GpsTracking} />} />
                        <Route path="/webmail" element={<Shell Page={Webmail} />} />
                        <Route path="/partners" element={<Shell Page={Partners} />} />
                        <Route path="/users" element={<Shell Page={Users} roles={["company_admin", "super_admin"]} />} />
                        <Route path="/audit" element={<Shell Page={AuditLogs} roles={["company_admin", "super_admin"]} />} />
                        <Route path="/settings" element={<Shell Page={Settings} />} />
                        <Route path="/settings/billing" element={<Shell Page={Billing} roles={["company_admin", "super_admin"]} />} />
                        <Route path="/billing" element={<Shell Page={Billing} roles={["company_admin", "super_admin"]} />} />
                        <Route path="/admin" element={<Shell Page={SuperAdmin} roles={["super_admin"]} />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
